/**
 * Composed layouts — more than a caption and a device: a glow, a diagonal, a stat, a second
 * device.
 *
 * These are where the layer set earns its keep. Two constraints shaped them:
 *
 * - **Box coordinates are `0..1`**, so a shape cannot be positioned outside the canvas. A bleed
 *   is achieved with `rotation`, which pushes a shape's corners past the edges without needing a
 *   negative offset.
 * - **Nothing may assume a square canvas.** An ellipse is only a circle at one aspect ratio, so
 *   badges here are pills and rounded rects, which read correctly on all four families.
 */
import { Template } from '@keymaker/schema';
import { bandBelow, device, fittedDevice, gradient, radial, shape, text } from '../kit';

const CAPTION = { x: 0.045, y: 0.035, width: 0.91, height: 0.10 };

export const featurePills = Template.parse({
  id: 'feature-pills',
  name: 'Feature pills',
  category: 'composed',
  description: 'Three labelled pills under the headline, then the device.',
  tags: ['feature', 'list', 'benefit'],
  scene: {
    version: 1,
    canvas: { width: 1320, height: 2868 },
    background: gradient(['#0B3B8F', '#141033']),
    layers: [
      text({ content: '{{caption.1}}', box: CAPTION, size: 0.038 }),
      ...[0, 1, 2].flatMap((index) => {
        const y = 0.155 + index * 0.072;
        return [
          shape({
            id: `pill-${index + 1}`,
            kind: 'rounded-rect',
            box: { x: 0.06, y, width: 0.88, height: 0.058 },
            radius: 0.5,
            fill: '#FFFFFF22',
          }),
          text({
            id: `pill-label-${index + 1}`,
            content: `{{values.pill${index + 1}}}`,
            box: { x: 0.085, y: y + 0.006, width: 0.83, height: 0.046 },
            size: 0.018,
            weight: 600,
            align: 'left',
            valign: 'middle',
          }),
        ];
      }),
      fittedDevice({ top: bandBelow({ y: 0.155 + 2 * 0.072, height: 0.058 }) }),
    ],
  },
});

export const spotlight = Template.parse({
  id: 'spotlight',
  name: 'Spotlight',
  category: 'composed',
  description: 'A wide glow behind the device, with the caption above it.',
  tags: ['hero', 'premium', 'glow'],
  scene: {
    version: 1,
    canvas: { width: 1320, height: 2868 },
    background: radial(
      [
        { offset: 0, color: '#33429E' },
        { offset: 0.45, color: '#141A3C' },
        { offset: 1, color: '#070912' },
      ],
      0.58,
      // Centred on the device, not on the canvas, so the light reads as coming from behind it.
      { x: 0.5, y: 0.62 },
    ),
    layers: [
      text({ content: '{{caption.1}}', box: CAPTION, size: 0.040, align: 'center' }),
      fittedDevice({
        top: bandBelow(CAPTION),
        side: 0.04,
        shadow: { blur: 0.06, opacity: 0.55, offsetY: 0.012, color: '#000000' },
      }),
    ],
  },
});

export const diagonalSplit = Template.parse({
  id: 'diagonal-split',
  name: 'Diagonal split',
  category: 'composed',
  description: 'A two-tone canvas split on a slight diagonal, with the caption on the upper tone.',
  tags: ['bold', 'geometric', 'brand'],
  scene: {
    version: 1,
    canvas: { width: 1320, height: 2868 },
    background: gradient(['#8B1D4A', '#3A0B22']),
    layers: [
      // Rotation is what pushes the corners past the canvas edge; a negative box offset cannot.
      // The tint has to be strong enough to read as a deliberate panel — at 8% alpha the diagonal
      // was invisible in the contact sheet, which is the failure mode a geometry check cannot see.
      shape({
        id: 'split',
        kind: 'rect',
        box: { x: 0.0, y: 0.0, width: 1.0, height: 0.48 },
        rotation: -8,
        fill: '#FFE9F2D9',
      }),
      text({
        id: 'caption',
        content: '{{caption.1}}',
        box: CAPTION,
        size: 0.040,
        color: '#3A0B22',
      }),
      fittedDevice({ top: bandBelow(CAPTION) }),
    ],
  },
});

export const twoDevices = Template.parse({
  id: 'two-devices',
  name: 'Two devices',
  category: 'composed',
  description:
    'A second, smaller device set behind the first, to suggest a flow rather than a single screen.',
  tags: ['flow', 'pair', 'depth'],
  scene: {
    version: 1,
    canvas: { width: 1320, height: 2868 },
    background: gradient(['#124E7A', '#0A1B33']),
    layers: [
      text({ content: '{{caption.1}}', box: CAPTION, size: 0.038 }),
      // The rear device sits high and right, the front one low and left, so both read as whole
      // devices. Placing them by scale rather than by a band is what allows the overlap.
      device({
        id: 'device-back',
        scale: 0.40,
        x: 0.78,
        y: 0.50,
        rotation: 4,
        shadow: { blur: 0.04, opacity: 0.45, offsetY: 0.008, color: '#04121F' },
      }),
      device({
        id: 'device-front',
        scale: 0.62,
        x: 0.31,
        y: 0.68,
        anchor: 'center',
        rotation: -2,
        shadow: { blur: 0.06, opacity: 0.5, offsetY: 0.012, color: '#04121F' },
      }),
    ],
  },
});

export const badgeCount = Template.parse({
  id: 'badge-count',
  name: 'Badge and count',
  category: 'composed',
  description: 'A stat pill above the headline, then the device. Reads as proof rather than a claim.',
  tags: ['proof', 'metric', 'badge'],
  scene: {
    version: 1,
    canvas: { width: 1320, height: 2868 },
    background: radial([
      { offset: 0, color: '#1E3A8A' },
      { offset: 0.6, color: '#0C1A44' },
      { offset: 1, color: '#050A20' },
    ]),
    layers: [
      shape({
        id: 'badge',
        kind: 'rounded-rect',
        box: { x: 0.045, y: 0.035, width: 0.34, height: 0.052 },
        radius: 0.5,
        fill: '#FFD166',
      }),
      text({
        id: 'badge-label',
        content: '{{values.badge}}',
        box: { x: 0.045, y: 0.04, width: 0.34, height: 0.042 },
        size: 0.017,
        weight: 700,
        color: '#1A1206',
        align: 'center',
        valign: 'middle',
      }),
      text({ content: '{{caption.1}}', box: { ...CAPTION, y: 0.105 }, size: 0.038 }),
      fittedDevice({ top: bandBelow({ y: 0.105, height: 0.10 }) }),
    ],
  },
});

export const composedTemplates = [featurePills, spotlight, diagonalSplit, twoDevices, badgeCount];
